import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import ConfirmModal from '../components/ConfirmModal';
import styles from './TablePage.module.css';

type Status = 'todo' | 'in_progress' | 'done';

type Todo = {
  id: string;
  title: string;
  description: string | null;
  status: Status;
  due_date: string | null;
  user_id: string;
  created_at: string;
  updated_at: string;
};

const STATUS_LABEL: Record<Status, string> = {
  todo: '할 일',
  in_progress: '진행 중',
  done: '완료',
};



const STATUS_ACCENT: Record<Status, string> = {
  todo: '#A78BFA',
  in_progress: '#E8649A',
  done: '#34C98A',
};

const STATUS_NOTE_BG: Record<Status, string> = {
  todo: 'rgba(40,30,70,0.9)',
  in_progress: 'rgba(55,25,55,0.9)',
  done: 'rgba(20,50,40,0.9)',
};

const EMPTY = { title: '', description: '', status: 'todo' as Status, due_date: '' };

export default function TodosPage() {
  const [todos, setTodos] = useState<Todo[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [filterMine, setFilterMine] = useState(true);
  const [filterStatus, setFilterStatus] = useState<Status | 'all'>('all');
  const [form, setForm] = useState<any>(null);
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Todo | null>(null);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setCurrentUser(data.user));
  }, []);

  const fetchTodos = useCallback(async () => {
    setLoading(true);
    let query = supabase
      .from('todos')
      .select('*')
      .order('due_date', { ascending: true, nullsFirst: false })
      .order('created_at', { ascending: false });

    const { data } = await query;
    setTodos(data ?? []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchTodos(); }, [fetchTodos]);

  const filtered = todos.filter(t => {
    if (filterMine && t.user_id !== currentUser?.id) return false;
    if (filterStatus !== 'all' && t.status !== filterStatus) return false;
    return true;
  });

  const counts = {
    all: todos.filter(t => !filterMine || t.user_id === currentUser?.id).length,
    todo: todos.filter(t => t.status === 'todo' && (!filterMine || t.user_id === currentUser?.id)).length,
    in_progress: todos.filter(t => t.status === 'in_progress' && (!filterMine || t.user_id === currentUser?.id)).length,
    done: todos.filter(t => t.status === 'done' && (!filterMine || t.user_id === currentUser?.id)).length,
  };

  function openCreate() {
    setForm({ ...EMPTY });
    setCreating(true);
  }

  function openEdit(t: Todo) {
    setForm({ ...t, due_date: t.due_date ? t.due_date.substring(0, 10) : '' });
    setCreating(false);
  }

  async function handleSave() {
    const { id, created_at, updated_at, ...rest } = form;
    const payload = {
      ...rest,
      due_date: rest.due_date || null,
      description: rest.description || null,
      updated_at: new Date().toISOString(),
    };

    if (creating) {
      const { error } = await supabase.from('todos').insert([{
        ...payload,
        user_id: currentUser?.id ?? null,
        created_by: currentUser?.id ?? null,
      }]);
      if (error) { alert(error.message); return; }
    } else {
      const { error } = await supabase.from('todos').update(payload).eq('id', id);
      if (error) { alert(error.message); return; }
    }
    setForm(null);
    fetchTodos();
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    await supabase.from('todos').delete().eq('id', deleteTarget.id);
    setDeleteTarget(null);
    fetchTodos();
  }

  async function setStatus(t: Todo, status: Status) {
    if (t.status === status) return;
    await supabase.from('todos').update({ status, updated_at: new Date().toISOString() }).eq('id', t.id);
    fetchTodos();
  }

  if (loading) return <div className={styles.loading}>로딩 중...</div>;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>✅ 할 일</h1>
          <span className={styles.count}>전체 {counts.all}개 · 진행 중 {counts.in_progress}개 · 완료 {counts.done}개</span>
          <div className={styles.dbBadge}>🗄 todos</div>
        </div>
        <div className={styles.actions}>
          <button className="btn btn-ghost" onClick={fetchTodos}>↺</button>
          <button className="btn btn-primary" onClick={openCreate}>+ 할 일 추가</button>
        </div>
      </div>

      {/* Filter bar */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', background: 'rgba(255,255,255,0.1)', borderRadius: 999, padding: 3, gap: 2 }}>
          <button
            onClick={() => setFilterMine(true)}
            style={chipStyle(filterMine)}>내 할 일</button>
          <button
            onClick={() => setFilterMine(false)}
            style={chipStyle(!filterMine)}>전체</button>
        </div>
        <div style={{ display: 'flex', background: 'rgba(255,255,255,0.1)', borderRadius: 999, padding: 3, gap: 2 }}>
          {(['all', 'todo', 'in_progress', 'done'] as const).map(s => (
            <button key={s} onClick={() => setFilterStatus(s)} style={chipStyle(filterStatus === s)}>
              {s === 'all' ? '전체' : STATUS_LABEL[s]}
              {s !== 'all' && <span style={{ marginLeft: 4, opacity: 0.7, fontSize: 10 }}>{counts[s]}</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Todo grid — post-it style */}
      {filtered.length === 0 ? (
        <div className={styles.empty}>
          <div className={styles.emptyIcon}>📭</div>
          <div>할 일이 없습니다</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
          {filtered.map(t => {
            const accent = STATUS_ACCENT[t.status];
            return (
              <div key={t.id} style={{
                width: 220,
                minHeight: 260,
                background: STATUS_NOTE_BG[t.status],
                borderRadius: 4,
                borderTop: `4px solid ${accent}`,
                boxShadow: `0 4px 18px rgba(0,0,0,0.35), 0 1px 4px rgba(0,0,0,0.2)`,
                display: 'flex',
                flexDirection: 'column',
                padding: '18px 16px 14px',
                opacity: t.status === 'done' ? 0.6 : 1,
                transition: 'opacity 0.2s',
                position: 'relative',
              }}>
                {/* Status bar */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {(['todo', 'in_progress', 'done'] as Status[]).map(s => {
                      const isActive = t.status === s;
                      const color = STATUS_ACCENT[s];
                      return (
                        <button
                          key={s}
                          onClick={() => setStatus(t, s)}
                          style={{
                            background: isActive ? `${color}33` : 'rgba(0,0,0,0.15)',
                            border: `1px solid ${isActive ? color + '88' : 'rgba(255,255,255,0.1)'}`,
                            borderRadius: 999,
                            padding: '3px 9px',
                            fontSize: 10,
                            fontWeight: isActive ? 800 : 600,
                            color: isActive ? color : 'rgba(255,255,255,0.35)',
                            cursor: 'pointer',
                            letterSpacing: 0.3,
                            transition: 'all 0.15s',
                          }}>
                          {STATUS_LABEL[s]}
                        </button>
                      );
                    })}
                  </div>
                  {t.due_date && (
                    <span style={{
                      fontSize: 10, fontWeight: 700,
                      color: isDueSoon(t.due_date, t.status) ? '#F07880' : 'rgba(255,255,255,0.35)',
                    }}>
                      {new Date(t.due_date).toLocaleDateString('ko-KR', { month: '2-digit', day: '2-digit' })}
                    </span>
                  )}
                </div>

                {/* Title */}
                <div style={{
                  fontSize: 15, fontWeight: 800, color: 'white',
                  textDecoration: t.status === 'done' ? 'line-through' : 'none',
                  lineHeight: 1.4, marginBottom: 10, flex: 0,
                }}>
                  {t.title}
                </div>

                {/* Description */}
                {t.description && (
                  <div style={{
                    fontSize: 12, color: 'rgba(255,255,255,0.55)',
                    lineHeight: 1.6, whiteSpace: 'pre-wrap', flex: 1,
                  }}>
                    {t.description}
                  </div>
                )}

                {/* Spacer */}
                <div style={{ flex: 1 }} />

                {/* Actions */}
                <div style={{ display: 'flex', gap: 6, marginTop: 14, justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => openEdit(t)}
                    style={{
                      background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.18)',
                      borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 700,
                      color: 'rgba(255,255,255,0.7)', cursor: 'pointer',
                    }}>편집</button>
                  <button
                    onClick={() => setDeleteTarget(t)}
                    style={{
                      background: 'rgba(232,100,100,0.15)', border: '1px solid rgba(232,100,100,0.3)',
                      borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 700,
                      color: '#F07880', cursor: 'pointer',
                    }}>삭제</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {form && (
        <div className="modal-overlay" onClick={() => setForm(null)}>
          <div className="modal-box" style={{ width: 520 }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
              <h2 style={{ fontSize: 18, fontWeight: 800 }}>{creating ? '할 일 추가' : '할 일 편집'}</h2>
              <button className="btn btn-ghost" style={{ padding: '4px 8px' }} onClick={() => setForm(null)}>✕</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={labelStyle}>제목</label>
                <input className="input" value={form.title}
                  onChange={e => setForm((p: any) => ({ ...p, title: e.target.value }))} />
              </div>
              <div>
                <label style={labelStyle}>내용 (선택)</label>
                <textarea className="input" rows={3} style={{ resize: 'vertical' }}
                  value={form.description ?? ''}
                  onChange={e => setForm((p: any) => ({ ...p, description: e.target.value }))} />
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>상태</label>
                  <select className="input" value={form.status}
                    onChange={e => setForm((p: any) => ({ ...p, status: e.target.value }))}>
                    <option value="todo">할 일</option>
                    <option value="in_progress">진행 중</option>
                    <option value="done">완료</option>
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>마감일 (선택)</label>
                  <input className="input" type="date" value={form.due_date ?? ''}
                    onChange={e => setForm((p: any) => ({ ...p, due_date: e.target.value }))} />
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 24, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => setForm(null)}>취소</button>
              <button className="btn btn-primary" onClick={handleSave}>{creating ? '추가' : '저장'}</button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <ConfirmModal title="할 일 삭제" message={`"${deleteTarget.title}"을 삭제할까요?`}
          onConfirm={handleDelete} onCancel={() => setDeleteTarget(null)} />
      )}
    </div>
  );
}

function isDueSoon(due: string, status: Status): boolean {
  if (status === 'done') return false;
  const diff = new Date(due).getTime() - Date.now();
  return diff < 2 * 24 * 60 * 60 * 1000; // within 2 days
}

function chipStyle(active: boolean): React.CSSProperties {
  return {
    padding: '5px 14px',
    borderRadius: 999,
    border: 'none',
    background: active ? 'rgba(255,255,255,0.22)' : 'transparent',
    color: active ? 'white' : 'rgba(255,255,255,0.5)',
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
    transition: 'all 0.15s',
  };
}

const labelStyle: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.6)',
  letterSpacing: 1, display: 'block', marginBottom: 6,
};
